import {Channel, XaccelStreamMetadata, CalChannel, StreamStats} from "@/model/models";

export function parseMetadata(providersMetadata: string | null | undefined): Record<string, any> {
    if (!providersMetadata) return {};
    try {
        return JSON.parse(providersMetadata);
    } catch (e) {
        console.warn('Invalid providersMetadata JSON', e);
        return {};
    }
}

interface ValidationResult {
    isValid: boolean;
    error: string;
}

/**
 * Gets XaccelStreamMetadata from channel's providersMetadata
 */
export function getChannelMetadata(channel: Channel): XaccelStreamMetadata | null {
    const parsed = parseMetadata(channel.providersMetadata);

    // Check if parsed object has the required XaccelStreamMetadata properties
    if (!parsed || typeof parsed !== 'object' || !('id' in parsed)) {
        return null;
    }

    return parsed as XaccelStreamMetadata;
}

/**
 * Extracts the stream name from an RTMP URL
 * Example: rtmp://eu-1.procricket.cloud/procricket/stream_1?key=654321&id=1 -> stream_1
 */
export function extractStreamNameFromUrl(url: string): string | null {
    try {
        // Remove query parameters first
        const urlWithoutQuery = url.split('?')[0];

        // Split by / and get the last segment
        const segments = urlWithoutQuery.split('/');
        const streamName = segments[segments.length - 1];

        return streamName || null;
    } catch {
        return null;
    }
}

/**
 * Validates if channel metadata output URL matches the channel name
 */
export function validateChannelStreamName(channel: Channel): ValidationResult {
    // Parse metadata from providersMetadata JSON string
    const metadata = getChannelMetadata(channel);

    // Check if channel has metadata
    if (!metadata) {
        return {
            isValid: false,
            error: "Channel has no metadata."
        };
    }

    // Check if metadata has output URL
    if (!metadata.output) {
        return {
            isValid: false,
            error: "Channel metadata has no output URL."
        };
    }

    // Extract stream name from URL
    const extractedStreamName = extractStreamNameFromUrl(metadata.output);

    if (!extractedStreamName) {
        return {
            isValid: false,
            error: "Could not extract stream name from output URL."
        };
    }

    // Check if channel has a name to compare against
    if (!channel.channelName) {
        return {
            isValid: false,
            error: "Channel has no name to validate against."
        };
    }

    // Compare extracted stream name with channel name
    if (extractedStreamName !== channel.channelName) {
        return {
            isValid: false,
            error: `Stream name mismatch: URL contains "${extractedStreamName}" but channel name is "${channel.channelName}".`
        };
    }

    return {
        isValid: true,
        error: ""
    };
}

/**
 * Complete eligibility check including stream name validation
 */
export function getStartEligibility(channel: Channel): { canStart: boolean; reason: string } {
    // 1) Already running
    if (channel.status === true) {
        return { canStart: false, reason: "Stream is already running." };
    }

    // 2) Must be active
    if (!channel.active) {
        return { canStart: false, reason: "Channel is not active." };
    }

    // 3) Must have metadata
    const metadata = getChannelMetadata(channel);
    if (!metadata) {
        return { canStart: false, reason: "Missing metadata." };
    }

    // 4) Must have provider (encoder) and stream id
    // if (!metadata.provider || !metadata.id) {
    //     console.log("metadata" + metadata)
    //     return { canStart: false, reason: "Missing provider or stream ID in metadata." };
    // }

    // 5) Validate stream name matches metadata output
    const streamValidation = validateChannelStreamName(channel);
    if (!streamValidation.isValid) {
        return { canStart: false, reason: streamValidation.error };
    }

    return { canStart: true, reason: "" };
}

// cal_channel.encStreamId is a string; the b-api takes a numeric stream_identifier when it is one.
export function encStreamIdentifier(encStreamId: string | number | null | undefined): number | string {
    const s = String(encStreamId ?? '').trim();
    return /^\d+$/.test(s) ? Number(s) : s;
}

export function kibanaDashboardUrl(channelName: string): string | null {
    const template = process.env.VUE_APP_KIBANA_DASHBOARD_URL_TEMPLATE || '';
    if (!template) {
        console.warn('Missing VUE_APP_KIBANA_DASHBOARD_URL_TEMPLATE');
        return null;
    }
    const safe = String(channelName || '').replace(/"/g, '\\"').replace(/'/g, "\\'");
    return template.replace('channelName : ""', `channelName : "${safe}"`);
}

export function statsMapFromList(stats: StreamStats[]): Map<string, StreamStats> {
    const map = new Map<string, StreamStats>();
    for (const s of stats) map.set(String(s.streamId), s);
    return map;
}

export function isLinkedChannel(channel: CalChannel): boolean {
    return !!(channel.encoder?.trim() && String(channel.encStreamId || '').trim());
}

export function isStreamRunning(status: string | number | boolean | null | undefined): boolean {
    const s = String(status ?? '').toLowerCase().trim();
    return s === 'true' || s === '1' || s === 'running' || s === 'active';
}

export function formatBitrate(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '-';
    const n = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(n)) return String(value);
    if (n >= 1000) return (n / 1000).toFixed(1) + ' Mbps';
    return n + ' kbps';
}

export function formatUptime(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'string') {
        const n = parseFloat(value);
        if (isNaN(n)) return value;
        value = n;
    }
    const secs = Math.floor(value as number);
    if (isNaN(secs)) return '-';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h}h ${m}m ${s}s`;
}

export function formatSpeed(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '-';
    const n = typeof value === 'string' ? parseFloat(value) : value as number;
    if (isNaN(n)) return '-';
    return n.toFixed(1) + 'x';
}

export function formatStatInt(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '-';
    const n = typeof value === 'number' ? value : parseInt(String(value), 10);
    if (isNaN(n)) return '-';
    return String(n);
}

export function buildOutputURL(selectedApp: string, origin: string, channel: string, eventId: string): string {
    const a = (selectedApp ?? "").trim();
    const o = (origin ?? "").trim();
    const c = (channel ?? "").trim();
    const e = String(eventId ?? "").trim();
    // If channel ends with "b" (e.g., channel_3b), use app path `${app}_b`
    const appPath = c.toLowerCase().endsWith("b") ? `${a}_b` : a;

    return `rtmp://${o}.${a}.cloud/${appPath}/${c}?key=654321&id=${e}`;
}

export type ParsedOutputUrl = {
    origin: string | null;
    destinationChannel: string | null;
};

/**
 * Parses an RTMP/HTTP(S) output URL and extracts:
 * - origin: the first hostname segment (e.g. "eu-origin1" from "eu-origin1.procricket.cloud")
 * - destinationChannel: the last path segment (e.g. "stream_4" from "/procricket/stream_4")
 */
export function parseOutputUrl(outputUrl: unknown): ParsedOutputUrl {
    if (typeof outputUrl !== "string" || !outputUrl.trim()) {
        return { origin: null, destinationChannel: null };
    }

    try {
        const url = new URL(outputUrl);

        const hostParts = url.hostname.split(".");
        const origin = hostParts.length ? hostParts[0] : null;

        const pathParts = url.pathname.split("/").filter(Boolean);
        const destinationChannel = pathParts.length
            ? pathParts[pathParts.length - 1]
            : null;

        return { origin, destinationChannel };
    } catch {
        // Keep it pure and predictable; caller can log if they want.
        return { origin: null, destinationChannel: null };
    }
}
const same = (a: unknown, b: unknown): boolean =>
    String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase()

/**
 * Compares a cal channel with its encoder stream (get-stream-metadata) and returns the
 * configuration problems found: missing/mismatching output URL, origin and stream name.
 */
export function checkChannelConfig(
    channel: { channelName?: string, origin?: string, encStreamName?: string },
    meta: { name?: string | null, output?: string | null } | null
): string[] {
    if (!meta) return []
    const issues: string[] = []
    const name = (channel.channelName || '').trim()
    const origin = (channel.origin || '').trim()
    const streamName = (channel.encStreamName || '').trim()

    if (!meta.output) {
        issues.push('The encoder stream has no output URL.')
    } else {
        const parsed = parseOutputUrl(meta.output)
        if (!parsed.destinationChannel) {
            issues.push('The output URL is not valid.')
        } else if (name && !same(parsed.destinationChannel, name)) {
            issues.push(`Output URL points to ${parsed.destinationChannel}, this channel is ${name}.`)
        }
        if (!origin) {
            issues.push('No origin is set on the channel.')
        } else if (parsed.origin && !same(parsed.origin, origin)) {
            issues.push(`Output URL origin is ${parsed.origin}, the channel has ${origin}.`)
        }
    }

    if (!streamName) {
        issues.push('No stream name is set on the channel.')
    } else if (meta.name && !same(meta.name, streamName)) {
        issues.push(`The encoder stream is named "${meta.name}", the channel has "${streamName}".`)
    }
    return issues
}

const LETTER_RANK: Record<string, number> = { base: 0, b: 1, c: 2, d: 3 }

function letterRank(channelName: string | undefined): number {
    const m = /^stream_(\d{1,2})([b-d])?$/i.exec(channelName || '')
    if (!m) return 4
    return LETTER_RANK[m[2] ? m[2].toLowerCase() : 'base']
}

export function sortChannelsByOrder(channels: CalChannel[]): CalChannel[] {
    return channels.slice().sort((a, b) => {
        const ra = letterRank(a.channelName)
        const rb = letterRank(b.channelName)
        if (ra !== rb) return ra - rb
        return (a.calChannelId || 0) - (b.calChannelId || 0)
    })
}
